'use client';

import React from 'react';

import { toast } from 'sonner';

import {
  Truck,
  Phone,
  Loader2,
  MessageSquare,
  ClipboardCheck,
  Boxes,
} from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';

import Requests from '@/lib/requests';

import OtpInput from '@/components/auth/OtpInput';

export default function LoginPage() {
  const [phoneNumber, setPhoneNumber] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [formType, setFormType] = React.useState<'login' | 'otp'>('login');

  async function onSubmit(event: any) {
    event.preventDefault();
    setIsLoading(true);

    try {
      const request = new Requests();
      const response = await request.post('/api/gen-otp', {
        phone: Number(`91${phoneNumber}`),
      });

      if (response.status) {
        setFormType('otp');
        return;
      } else {
        toast.error(response.error);
        return;
      }
    } catch (error: any) {
      
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className='min-h-screen relative flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-red-50'>
      {/* Subtle Background Pattern */}
      <div className='absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,red_1px,transparent_0)] bg-[size:40px_40px] opacity-[0.03]'></div>
      {/* Content */}
      <div className='relative w-full max-w-6xl mx-auto px-4 py-12 flex flex-col lg:flex-row items-center justify-between gap-12'>
        {/* Left side - Branding */}
        <div className='space-y-8 lg:w-1/2'>
          <div className='flex items-center gap-4'>
            <div className='h-16 w-16 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center shadow-lg'>
              <Truck className='h-8 w-8 text-white' />
            </div>
            <div>
              <h2 className='text-3xl font-bold text-gray-900'>Autowhat AI</h2>
              <p className='text-red-600 font-medium'>
                Transport Automation Platform
              </p>
            </div>
          </div>
          <div className='space-y-4'>
            <h1 className='text-4xl font-bold leading-tight text-gray-900'>
              Enterprise-Grade
              <span className='block text-red-600'>
                Transportation Management
              </span>
            </h1>
            <p className='text-lg text-gray-600 border-l-4 border-red-500 pl-4'>
              Streamline your logistics operations with automated WhatsApp
              communication and customizable Standard Operating Procedures
            </p>
          </div>
          <div className='grid grid-cols-1 gap-6'>
            <div className='bg-white rounded-xl p-6 shadow-md border border-red-100 flex items-start gap-4'>
              <div className='h-12 w-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0'>
                <MessageSquare className='h-6 w-6 text-red-600' />
              </div>
              <div>
                <h3 className='font-semibold text-gray-900 text-lg'>
                  WhatsApp Integration
                </h3>
                <p className='text-gray-600'>
                  Automated real-time updates and communications through
                  WhatsApp Business API
                </p>
              </div>
            </div>
            <div className='bg-white rounded-xl p-6 shadow-md border border-red-100 flex items-start gap-4'>
              <div className='h-12 w-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0'>
                <ClipboardCheck className='h-6 w-6 text-red-600' />
              </div>
              <div>
                <h3 className='font-semibold text-gray-900 text-lg'>
                  Custom SOPs
                </h3>
                <p className='text-gray-600'>
                  Tailored arrival and departure procedures with digital
                  checklists and verification
                </p>
              </div>
            </div>
            <div className='bg-white rounded-xl p-6 shadow-md border border-red-100 flex items-start gap-4'>
              <div className='h-12 w-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0'>
                <Boxes className='h-6 w-6 text-red-600' />
              </div>
              <div>
                <h3 className='font-semibold text-gray-900 text-lg'>
                  Hub & Spoke Management
                </h3>
                <p className='text-gray-600'>
                  Centralized control with customizable workflows for each
                  location
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Right side - Login Form */}
        <Card className='w-full max-w-md bg-white shadow-2xl border-red-100'>
          <CardContent className='p-8'>
            <div className='space-y-6'>
              <div className='text-center'>
                <h2 className='text-2xl font-bold text-gray-900'>
                  Enterprise Login
                </h2>
                <p className='text-gray-600 mt-2'>
                  Access your transportation dashboard
                </p>
              </div>

              {formType === 'login' ? (
                <form onSubmit={onSubmit} className='space-y-6'>
                  <div className='space-y-2'>
                    <label className='text-gray-700 font-medium block'>
                      Business Phone Number
                    </label>
                    <div className='relative'>
                      <div className='flex'>
                        <span className='inline-flex items-center px-4 border border-r-0 border-red-200 bg-red-50 text-gray-600 rounded-l-md'>
                          <Phone className='h-5 w-5 text-red-500 mr-2' /> +91
                        </span>
                        <input
                          type='tel'
                          placeholder='Enter your phone number'
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          className='flex-1 block w-full rounded-r-md border border-red-200 px-3 py-2 focus:border-red-500 focus:ring-red-500 sm:text-sm'
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    type='submit'
                    disabled={isLoading}
                    className='w-full flex justify-center py-3 px-4 rounded-md shadow-md text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200'
                  >
                    {isLoading ? (
                      <Loader2 className='h-5 w-5 animate-spin' />
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </form>
              ) : (
                <OtpInput phoneNumber={Number(phoneNumber)} />
              )}

              <div className='text-center text-sm'>
                <p className='text-gray-600'>
                  Technical Support:
                  <a
                    href='mailto:chat@autowhat.app'
                    className='text-red-600 hover:text-red-700 font-medium'
                  >
                    chat@autowhat.app
                  </a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
